import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useOAuth, OAuthTokens } from '@/common/hooks/useOAuth';
import { Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { InstagramPagesLoader } from './InstagramPagesLoader';
import { useWebDispatch, useWebSelector } from '@/website/store/web-store';
import {
  setOAuthTokens,
  clearOAuthTokens,
  selectOAuthTokens,
  selectOAuthLoading,
  selectOAuthError
} from '@/common/reducers/OAuthTestSlice';
import AuthTokensTable from './AuthTokensTable';
import { useAppSelector } from '../reducers/hooks/store.hook';
import { selectUserId } from '../reducers/UserSelectors';

interface OAuthServiceProps {
  serviceName: string;
  authEndpoint: string;
  refreshEndpoint: string;
  successMessageType: string;
  errorMessageType: string;
  botId: string;
  scopes?: string;
  userId: string;
}

const OAuthService: React.FC<OAuthServiceProps> = ({
  serviceName,
  authEndpoint,
  refreshEndpoint,
  successMessageType,
  errorMessageType,
  botId,
  userId,
  scopes,
}) => {
  // Get provider from serviceName (lowercase)
  const provider = serviceName.toLowerCase();

  // Redux state
  const dispatch = useWebDispatch();
  const tokens = useWebSelector(state => selectOAuthTokens(state, provider));
  const isLoading = useWebSelector(state => selectOAuthLoading(state, provider));
  const error = useWebSelector(state => selectOAuthError(state, provider));

  // Convert tokens to the expected OAuthTokens format
  const oauthTokens = tokens ? {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    tokenExpiry: tokens.tokenExpiry
  } : null;

  // Use useCallback to prevent the callback from being recreated on each render
  const handleTokensUpdate = useCallback((newTokens: OAuthTokens) => {
    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`${serviceName} tokens updated:`, newTokens);
    }

    // Update redux store with tokens that match the OAuthTokenData interface
    dispatch(setOAuthTokens({
      provider: provider,
      tokens: {
        accessToken: newTokens.accessToken ?? '',
        refreshToken: newTokens.refreshToken,
        tokenExpiry: newTokens.tokenExpiry,
        expiresIn: newTokens.expiresIn,
        email: newTokens.email,
        provider
      }
    }));
  }, [serviceName, provider, dispatch]);

  const oauth = useOAuth(
    {
      toolName: serviceName,
      authEndpoint,
      refreshEndpoint,
      successMessageType,
      errorMessageType,
      additionalAuthData: {
        userId
      }
    },
    oauthTokens as OAuthTokens,
    handleTokensUpdate
  );

  // Sync OAuth state with Redux
  useEffect(() => {
    if (oauth.state.error && !error) {
      console.error(`${serviceName} OAuth error:`, oauth.state.error);
    }
  }, [oauth.state, error, serviceName]);

  const handleConnect = useCallback(() => {
    if (!botId) {
      console.error('Bot ID is required to connect');
      return;
    }

    const connectParams: Record<string, any> = { botId };

    // Add scopes if provided
    if (scopes) {
      connectParams.scope = scopes;
    }

    oauth.connect(connectParams);
  }, [botId, oauth, scopes]);

  const handleDisconnect = useCallback(() => {
    oauth.disconnect();
    dispatch(clearOAuthTokens({ provider }));
  }, [oauth, dispatch, provider]);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg">{serviceName}</CardTitle>
        <CardDescription>
          Connect to {serviceName} to test OAuth functionality.
        </CardDescription>
      </CardHeader>

      <CardContent>
        {oauth.state.isConnected && !oauth.state.isExpired && oauth.state.email && (
          <div className="flex items-center space-x-2 text-sm">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span>Connected as <strong>{oauth.state.email}</strong></span>
          </div>
        )}

        {oauth.state.isConnected && oauth.state.isExpired && oauth.state.email && (
          <div className="flex items-center space-x-2 text-sm">
            <XCircle className="h-5 w-5 text-amber-500" />
            <span>Token expired for <strong>{oauth.state.email}</strong></span>
          </div>
        )}

        {!oauth.state.isConnected && (
          <div className="flex items-center space-x-2 text-sm">
            <XCircle className="h-5 w-5 text-gray-400" />
            <span>Not connected to {serviceName}</span>
          </div>
        )}

        {oauth.state.error && (
          <div className="mt-2 text-sm text-red-500">
            Error: {oauth.state.error}
          </div>
        )}

        {tokens && (
          <div className="mt-4 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-40">
            <pre>{JSON.stringify(tokens, null, 2)}</pre>
          </div>
        )}
      </CardContent>

      <CardFooter className="flex justify-between">
        {!oauth.state.isConnected ? (
          <Button
            onClick={handleConnect}
            disabled={oauth.state.isConnecting}
            className="w-full"
          >
            {oauth.state.isConnecting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              `Connect ${serviceName}`
            )}
          </Button>
        ) : (
          <div className="flex w-full space-x-2">
            {oauth.state.isExpired && (
              <Button
                onClick={() => oauth.refreshToken()}
                disabled={oauth.state.isConnecting}
                variant="outline"
                className="flex-1"
              >
                {oauth.state.isConnecting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Refresh Token
              </Button>
            )}

            <Button
              onClick={handleDisconnect}
              variant="destructive"
              className="flex-1"
            >
              Disconnect
            </Button>
          </div>
        )}
      </CardFooter>
    </Card>
  );
};

export const OAuthTest: React.FC = () => {
  const [botId, setBotId] = useState<string>('test-bot-id');
  const userId = useAppSelector(selectUserId);

  if (!userId) {
    return (
      <div className="container mx-auto p-4 max-w-3xl">
        <h1 className="text-2xl font-bold mb-6">OAuth Integration Test</h1>
        <p>Please log in to test OAuth integrations.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-3xl">
      {/* Toaster is provided globally by BTThemeProvider (sonner) — no local instance needed */}

      <h1 className="text-2xl font-bold mb-6">OAuth Integration Test</h1>

      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Bot ID for Testing</label>
        <input
          type="text"
          value={botId}
          onChange={(e) => setBotId(e.target.value)}
          className="w-full p-2 border rounded"
        />
      </div>

      <Tabs defaultValue="gmail">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="gmail">Gmail</TabsTrigger>
          <TabsTrigger value="drive">Google Drive</TabsTrigger>
          <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
          <TabsTrigger value="facebook">Facebook</TabsTrigger>
          <TabsTrigger value="instagram">Instagram</TabsTrigger>
        </TabsList>

        <TabsContent value="gmail" className="mt-4">
          <OAuthService
            serviceName="Gmail"
            authEndpoint="/auth/gmail"
            refreshEndpoint="/auth/gmail/refresh"
            successMessageType="gmail_auth_success"
            errorMessageType="gmail_auth_error"
            botId={botId}
            userId={userId}
          />
        </TabsContent>

        <TabsContent value="drive" className="mt-4">
          <OAuthService
            serviceName="Google Drive"
            authEndpoint="/auth/drive"
            refreshEndpoint="/auth/drive/refresh"
            successMessageType="drive_auth_success"
            errorMessageType="drive_auth_error"
            botId={botId}
            userId={userId}
          />
        </TabsContent>

        <TabsContent value="linkedin" className="mt-4">
          <OAuthService
            serviceName="LinkedIn"
            authEndpoint="/auth/linkedin"
            refreshEndpoint="/auth/linkedin/refresh"
            successMessageType="linkedin_auth_success"
            errorMessageType="linkedin_auth_error"
            botId={botId}
            userId={userId}
          />
        </TabsContent>

        <TabsContent value="facebook" className="mt-4">
          <OAuthService
            serviceName="Facebook"
            authEndpoint="/oauth/facebook"
            refreshEndpoint="/oauth/facebook/refresh"
            successMessageType="facebook_auth_success"
            errorMessageType="facebook_auth_error"
            botId={botId}
            scopes="pages_show_list,instagram_basic,pages_read_engagement,pages_messaging,pages_manage_metadata,instagram_manage_messages,instagram_manage_comments"
            userId={userId}
          />

          {/* Instagram Pages Section */}
          <div className="mt-6 border-t pt-4">
            <h3 className="text-md font-semibold mb-3">Instagram Connected Pages</h3>
            <InstagramPagesLoader />
          </div>
        </TabsContent>

        <TabsContent value="instagram" className="mt-4">
          <OAuthService
            serviceName="Instagram"
            authEndpoint="/oauth/instagram"
            refreshEndpoint="/oauth/instagram/refresh"
            successMessageType="instagram_auth_success"
            errorMessageType="instagram_auth_error"
            botId={botId}
            scopes="pages_show_list,instagram_basic,pages_read_engagement,pages_messaging,pages_manage_metadata,instagram_manage_messages"
            userId={userId}
          />
        </TabsContent>
      </Tabs>

      {/* Auth Tokens Table Section */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">Stored Authentication Tokens</h2>
        <p className="text-sm text-gray-600 mb-4">
          This table shows all OAuth tokens stored in the database for your account. You can manage and delete tokens as needed.
        </p>
        <AuthTokensTable />
      </div>

      <div className="mt-8 p-4 bg-gray-100 rounded">
        <h2 className="text-lg font-semibold mb-2">Debug Information</h2>
        <p className="text-sm text-gray-600 mb-2">
          This component demonstrates the reusable OAuth hook with Gmail, Google Drive, LinkedIn, Facebook, and Instagram.
          It shows how the same hook can be used for different services with minimal configuration.
        </p>
        <p className="text-sm text-gray-600 mb-2">
          <strong>Note:</strong> For Instagram access, Facebook login can be used to access connected Instagram accounts through the Graph API.
        </p>
        <ul className="list-disc list-inside text-sm text-gray-600">
          <li>Redux is used for global state management of auth tokens</li>
          <li>Each service uses its own endpoints and message types</li>
          <li>The Instagram component automatically detects Facebook authentication</li>
          <li>The UI automatically updates based on connection status</li>
          <li>Token storage is handled securely in the database</li>
        </ul>
      </div>
    </div>
  );
};

export default OAuthTest; 